import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, MapPin, Package, Sparkles, Loader2, AlertCircle, Plus, X } from "lucide-react";
import axios from "axios";
import { motion } from "framer-motion";
import { svcLabel } from "../lib/serviceLabel";
import { QuickOrderHeader } from "../components/QuickOrderHeader";
import { QOHoneypot, QO_LOAD_KEY, qoElapsedSeconds } from "../components/QOHoneypot";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = axios.create({ baseURL: `${BACKEND}/api`, timeout: 30000 });

export default function QuickOrder() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  // v85.19 — Multi-service: list of {service, qty}. Row pertama = primary.
  //          Row kedua dst. dibikin dgn service="" (customer wajib pilih sendiri).
  const [services, setServices] = useState([{ service: "service_cleaning", qty: 1 }]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [customAddr, setCustomAddr] = useState("");
  const [useCustom, setUseCustom] = useState(false);
  const [notes, setNotes] = useState("");
  const [showSummary, setShowSummary] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);   // {pending_order_id, slots[], options[], summary}
  const [confirmed, setConfirmed] = useState(null);
  const [dateError, setDateError] = useState("");
  const [unavailableSlots, setUnavailableSlots] = useState(new Set());   // v84.141 — slot yg race-condition (grayout)
  const [mapsLink, setMapsLink] = useState("");   // v84.141 — LINK LOKASI (auto-fill / manual / geo)
  const [mapsLinkAutoFilled, setMapsLinkAutoFilled] = useState(false);   // v84.142 — track apakah maps_link auto-fill dari history
  const [geoStatus, setGeoStatus] = useState("");   // "" | "loading" | "denied" | "unsupported" | "ok"
  const [website, setWebsite] = useState("");
  useEffect(() => { if (!sessionStorage.getItem(QO_LOAD_KEY)) sessionStorage.setItem(QO_LOAD_KEY, String(Date.now())); }, []);
  const slotsRef = useRef(null);   // v84.128 — auto-scroll ke area slot setelah preview
  const orderBtnRef = useRef(null);   // v84.140 — auto-scroll tombol Order Sekarang setelah customer pilih alamat

  useEffect(() => {
    API.get(`/public/quick-order/${token}`)
      .then(r => {
        // v84.143 — Normalize addresses: dukung old (string) + new ({address, maps_link})
        const rawAddrs = r.data.addresses || [];
        const normAddrs = rawAddrs.map(a =>
          typeof a === "string" ? { address: a, maps_link: null } : a
        );
        const nextInfo = { ...r.data, addresses: normAddrs };
        setInfo(nextInfo);
        // v84.167 — Rule Maps Sherlok init:
        //   • 1 alamat  → auto-select + auto-fill maps_link dari alamat itu.
        //   • ≥2 alamat → JANGAN auto-fill. User klik dulu, `selectAddress`
        //                 yang akan sync maps_link sesuai alamat terpilih.
        //                 Ini memperbaiki bug "field selalu tampilkan maps dari
        //                 alamat pertama meski user pilih alamat lain".
        //   • 0 alamat  → fallback customer-level `r.data.maps_link` (legacy).
        if (normAddrs.length === 1) {
          setSelectedAddr(normAddrs[0]);
          if (normAddrs[0].maps_link) {
            setMapsLink(normAddrs[0].maps_link);
            setMapsLinkAutoFilled(true);
          }
          return;
        }
        if (normAddrs.length === 0 && r.data.maps_link) {
          setMapsLink(r.data.maps_link);
          setMapsLinkAutoFilled(true);
        }
      })
      .catch(e => setErr(e?.response?.data?.detail || "Link tidak valid"));
  }, [token]);

  // v84.141 — Trigger browser Geolocation API + convert ke Google Maps URL
  function useMyLocation() {
    if (!navigator.geolocation) {
      setGeoStatus("unsupported");
      return;
    }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setMapsLink(`https://maps.google.com/?q=${latitude},${longitude}`);
        setGeoStatus("ok");
      },
      (err) => {
        console.warn("[QO-GEO] denied", err);
        setGeoStatus("denied");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }

  // v84.128 — Auto-scroll ke slot buttons setelah preview tersedia
  useEffect(() => {
    if (preview && slotsRef.current) {
      setTimeout(() => {
        try {
          slotsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_e) {}
      }, 150);
    }
  }, [preview]);

  // v84.140 — Auto-scroll ke tombol "Order Sekarang" setelah customer pilih alamat
  // (baik dari daftar existing atau isi manual). Supaya tombol tidak tersembunyi di bawah.
  useEffect(() => {
    if ((selectedAddr || (useCustom && customAddr.trim())) && orderBtnRef.current && !preview) {
      setTimeout(() => {
        try {
          orderBtnRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (_e) {}
      }, 200);
    }
     
  }, [selectedAddr, useCustom]);   // sengaja tdk depend customAddr biar tidak spam scroll saat ketik

  function activeAddress() {
    return useCustom ? customAddr.trim() : (selectedAddr?.address || null);
  }

  // v84.143 — Sinkronkan Maps Sherlok mengikuti alamat yg dipilih customer
  function selectAddress(item) {
    setSelectedAddr(item);
    setUseCustom(false);
    if (item?.maps_link) {
      setMapsLink(item.maps_link);
      setMapsLinkAutoFilled(true);
    } else {
      setMapsLink("");
      setMapsLinkAutoFilled(false);
      setGeoStatus("");
    }
  }

  function chooseCustomAddress() {
    setUseCustom(true);
    setSelectedAddr(null);
    // Alamat baru → maps_link kosong, tombol geo muncul
    setMapsLink("");
    setMapsLinkAutoFilled(false);
    setGeoStatus("");
  }

  // v85.19 — Multi-service helpers
  function addServiceRow() {
    // Baris baru dgn field KOSONG — customer klik untuk buka dropdown.
    setServices(prev => [...prev, { service: "", qty: 1 }]);
  }
  function removeServiceRow(idx) {
    setServices(prev => prev.filter((_, i) => i !== idx));
  }
  function updateServiceRow(idx, patch) {
    setServices(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }
  function handleServiceChange(idx, v) {
    // Pop-up garansi warning tetap dipakai jika customer pilih "Klaim garansi"
    if (v === "Klaim garansi") {
      const ws = info?.warranty_status;
      if (!ws) {
        alert("Anda belum pernah menggunakan layanan kami. Silahkan pilih layanan Perbaikan atau Service terlebih dahulu.");
        return;
      }
      if (!ws.eligible) {
        alert(
          "⚠️ Cover garansi anda sudah melewati masa berlaku, silahkan pilih layanan perbaikan.\n\n" +
          `Masa garansi ${ws.warranty_days} hari (${ws.previous_service_type}) sejak ${ws.previous_wo_date}.\n` +
          `Sudah terlewat ${ws.elapsed_days - ws.warranty_days} hari.`
        );
        updateServiceRow(idx, { service: "Perbaikan" });
        return;
      }
    }
    updateServiceRow(idx, { service: v });
  }

  async function submitOrder() {
    const addr = activeAddress();
    if (!addr) {
      alert("Silakan pilih alamat terlebih dahulu");
      return;
    }
    // v85.19 — Validate: minimal 1 service terpilih (bukan string kosong).
    const picked = services.filter(r => (r.service || "").trim());
    if (picked.length === 0) {
      alert("Silakan pilih layanan terlebih dahulu");
      return;
    }
    const primary = picked[0];
    setBusy(true);
    setDateError("");
    setUnavailableSlots(new Set());
    try {
      const r = await API.post(`/public/quick-order/${token}/preview`, {
        service_type: primary.service,
        quantity: primary.qty,
        address: addr,
        notes,
        maps_link: mapsLink || null,
        items: picked.map(p => ({ service_type: p.service, quantity: p.qty })),
      });
      if (!r.data.ok) {
        setDateError(r.data.message || "Tidak ada tanggal tersedia");
        setPreview(null);
      } else {
        setPreview(r.data);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Gagal submit order");
    } finally {
      setBusy(false);
    }
  }

  // v84.128 — Pick specific slot (day + time + tech). Legacy `pickDate(date)`
  // di-map ke first slot utk date tsb (backward compat).
  async function pickSlot(slot) {
    if (busy) return;
    // v84.141 — Guard: kalau slot sudah masuk unavailableSlots (race sblmnya), tolak.
    const slotKey = `${slot.date}|${slot.start_time}|${slot.tech_id || ""}`;
    if (unavailableSlots.has(slotKey)) return;
    setBusy(true);
    setDateError("");
    try {
      const r = await API.post(`/public/quick-order/${token}/confirm`, {
        pending_order_id: preview.pending_order_id,
        chosen_date: slot.date,
        chosen_start_time: slot.start_time,
        chosen_tech_id: slot.tech_id,
        maps_link: mapsLink || null,
        website,
        elapsed_seconds: qoElapsedSeconds(),
      });
      if (!r.data.ok) {
        setDateError(r.data.message || "Slot sudah tidak tersedia");
        // v84.141 — Tandai slot yg gagal sbg unavailable (grayout).
        setUnavailableSlots(prev => {
          const next = new Set(prev);
          next.add(slotKey);
          return next;
        });
        // Kalau backend kirim slots BARU (bukan sekedar duplikat lama), replace.
        // Deteksi: kalau tiap slot response berbeda dari yg ada di preview.slots → replace.
        if (Array.isArray(r.data.slots) && r.data.slots.length > 0) {
          const newSlotKeys = new Set(r.data.slots.map(s => `${s.date}|${s.start_time}|${s.tech_id || ""}`));
          const oldSlotKeys = new Set((preview.slots || []).map(s => `${s.date}|${s.start_time}|${s.tech_id || ""}`));
          const hasNewSlot = [...newSlotKeys].some(k => !oldSlotKeys.has(k));
          if (hasNewSlot) {
            setPreview({ ...preview, slots: r.data.slots });
            setUnavailableSlots(new Set());   // reset — semua slot baru
          }
        }
      } else {
        setConfirmed({
          ...r.data,
          address: preview.summary.address,
          chosen_date: slot.date,
          chosen_start_time: slot.start_time,
          service_type: preview.summary.service_type,
          quantity: preview.summary.quantity,
          items: preview.summary.items || null,
        });
      }
    } catch (e) {
      const st = e?.response?.status, det = e?.response?.data?.detail;
      if (st === 429) setDateError(det);
      else if (st === 400 && det === "Permintaan tidak valid") setDateError("Mohon tunggu sebentar, lalu pilih jadwal sekali lagi.");
      else alert(det || "Gagal konfirmasi");
    } finally {
      setBusy(false);
    }
  }

  if (err) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <div className="text-lg font-bold text-zinc-800 mb-1">Link tidak valid</div>
          <div className="text-sm text-zinc-600">{err}</div>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <QuickOrderHeader />
        <div className="flex items-center justify-center pt-24">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </div>
    );
  }

  if (confirmed) {
    // Defensive: validasi chosen_date sblm new Date() supaya tidak crash kalau
    // backend return null/invalid string.
    const rawDate = confirmed && confirmed.chosen_date;
    let dt = null;
    if (rawDate && typeof rawDate === "string") {
      const parsed = new Date(rawDate);
      if (!isNaN(parsed.getTime())) dt = parsed;
    }
    const dayId = dt ? ["Minggu","Senin","Selasa","Rabu","Kamis","Jumat","Sabtu"][dt.getDay()] : "";
    const monthId = dt ? ["Januari","Februari","Maret","April","Mei","Juni","Juli","Agustus","September","Oktober","November","Desember"][dt.getMonth()] : "";
    const dateLabel = dt
      ? `${dayId}, ${dt.getDate()} ${monthId}`
      : (rawDate || "-");
    // v84.131 — Format Jam dari chosen_start_time (HH:MM) → HH.MM
    const rawTime = confirmed && confirmed.chosen_start_time;
    let jamLabel = "-";
    if (rawTime && typeof rawTime === "string" && /^\d{2}:\d{2}$/.test(rawTime)) {
      jamLabel = rawTime.replace(":", ".");
    }
    return (
      <div className="min-h-screen bg-emerald-50 p-4 flex items-center justify-center" translate="no">
        <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 text-center">
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
          <div className="text-xl font-bold text-zinc-800 mb-4">✅ Pesanan Anda telah terjadwal.</div>
          <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-4 text-left space-y-1.5 text-sm" data-testid="qo-confirmed-summary">
            {(Array.isArray(confirmed.items) && confirmed.items.length > 1
              ? confirmed.items : [{ service_type: confirmed.service_type, quantity: confirmed.quantity }]
            ).map((it, i) => (
              <div key={i} translate="no">🛠️ : <b>{svcLabel(it.service_type)} {it.quantity} unit</b></div>
            ))}
            <div>📅 : <b>{dateLabel}</b></div>
            <div>⏰ : <b>{jamLabel}</b></div>
            <div>📍 : <b>{confirmed.address}</b></div>
          </div>
          <div className="text-sm text-zinc-600 mt-4" data-testid="qo-confirmed-tolerance">
            Estimasi Kedatangan teknisi memiliki toleransi waktu 1-2 jam ya kak, menyesuaikan kondisi operasional di lapangan. Terima kasih 🙏
          </div>
          <div className="text-xs text-zinc-500 mt-4">Terima kasih telah order via GLOBAL AC 🙏</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-8">
      <QuickOrderHeader />
      <motion.div
        className="px-5 py-4"
        initial={{ x: 80, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 30 }}
        data-testid="qo-content"
      >
        <QOHoneypot value={website} onChange={setWebsite} />
        <div className="text-sm text-zinc-700 mb-4">
          Halo <b>kak {info.customer_name || "customer"}</b>, silahkan order layanan yang kakak inginkan
        </div>

        {/* v85.19 — Multi-service picker. Row 1 = primary. Row 2+ = optional. */}
        <div className="mb-3" data-testid="qo-services">
          {services.map((row, idx) => {
            const isFirst = idx === 0;
            const serviceList = (info && Array.isArray(info.services)) ? info.services : [];
            return (
              <div key={idx} className="mb-3" data-testid={`qo-service-row-${idx}`}>
                <div className="grid grid-cols-[1fr_90px] gap-2 items-end">
                  {/* Layanan column */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                      {isFirst ? "Pilih Layanan" : "Layanan Lain"}
                    </label>
                    <select
                      value={row.service}
                      onChange={e => handleServiceChange(idx, e.target.value)}
                      className={`w-full h-11 rounded-lg border bg-white px-3 text-sm font-semibold ${
                        row.service ? "border-zinc-300 text-zinc-900" : "border-orange-400 text-orange-500"
                      }`}
                      data-testid={`qo-service-${idx}`}
                      translate="no">
                      {!row.service && <option value="">— Pilih —</option>}
                      {serviceList.map(s => <option key={s} value={s}>{svcLabel(s)}</option>)}
                    </select>
                  </div>
                  {/* Jumlah column */}
                  <div>
                    <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
                      Jumlah
                    </label>
                    <select value={row.qty}
                      onChange={e => updateServiceRow(idx, { qty: parseInt(e.target.value) })}
                      className="w-full h-11 rounded-lg border border-zinc-300 bg-white px-2 text-sm font-semibold"
                      data-testid={`qo-qty-${idx}`}>
                      {Array.from({ length: 10 }, (_, i) => i + 1).map(n =>
                        <option key={n} value={n}>{n}</option>
                      )}
                    </select>
                  </div>
                </div>
                {!isFirst && (
                  <div className="flex justify-end mt-1">
                    <button
                      type="button"
                      onClick={() => removeServiceRow(idx)}
                      className="flex flex-col items-center text-red-600 hover:text-red-700 active:scale-95 transition px-2"
                      data-testid={`qo-remove-${idx}`}>
                      <X className="w-5 h-5" strokeWidth={3} />
                      <span className="text-[10px] font-semibold leading-none mt-0.5">hapus</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add service button */}
          <button
            type="button"
            onClick={addServiceRow}
            className="flex items-center gap-2 mt-1 group"
            data-testid="qo-add-service">
            <div className="w-9 h-9 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-95 flex items-center justify-center shadow transition">
              <Plus className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <span className="text-[12px] font-semibold text-orange-600 group-hover:text-orange-700">
              Klik + untuk menambahkan layanan yang lain
            </span>
          </button>
        </div>

        {/* Address selection */}
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          Pilih Alamat
        </label>
        <div className="space-y-2 mb-4">
          {(info.addresses || []).map((a, i) => {
            const addrText = typeof a === "string" ? a : (a?.address || "");
            const isSel = !useCustom && selectedAddr?.address === addrText;
            return (
              <button key={i}
                onClick={() => selectAddress(typeof a === "string" ? { address: a, maps_link: null } : a)}
                className={`w-full text-left rounded-lg border-2 p-3 flex items-start gap-2 transition ${
                  isSel ? "border-orange-500 bg-orange-50" : "border-zinc-200 bg-white"
                }`}
                data-testid={`qo-addr-${i}`}>
                <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSel ? "text-orange-600" : "text-zinc-400"}`} />
                <div className="text-sm text-zinc-700 flex-1">{addrText}</div>
                {isSel && <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0" />}
              </button>
            );
          })}
          <button onClick={chooseCustomAddress}
            className={`w-full text-left rounded-lg border-2 border-dashed p-3 transition ${
              useCustom ? "border-orange-500 bg-orange-50" : "border-zinc-300 bg-white"
            }`}
            data-testid="qo-addr-custom-toggle">
            <div className="text-sm font-semibold text-zinc-700">+ Gunakan alamat lain</div>
          </button>
          {useCustom && (
            <textarea value={customAddr} onChange={e => setCustomAddr(e.target.value)}
              placeholder="Ketik alamat lengkap..."
              rows={2}
              className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm"
              data-testid="qo-addr-custom-input" />
          )}
        </div>

        {/* v84.142 — MAPS SHERLOK (auto-fill dari history, manual, atau via Geolocation)
             Rule: tampilkan helper text + tombol GEO HANYA saat field kosong.
             Begitu terisi (auto-fill / geo / manual), keduanya hilang & input memanjang penuh. */}
        <label className="block text-[11px] uppercase tracking-widest text-orange-500 font-bold mb-1">
          Maps Sherlok
        </label>
        {!mapsLink && (
          <div className="text-[11px] text-orange-400 font-semibold mb-2 leading-tight" data-testid="qo-geo-hint">
            Harap tekan "Sherlok" Untuk mengirim lokasi
          </div>
        )}
        <div className="flex flex-col [@media(min-width:400px)]:flex-row gap-2 mb-1">
          <input
            type="url"
            value={mapsLink}
            onChange={e => { setMapsLink(e.target.value); setMapsLinkAutoFilled(false); }}
            className="flex-1 min-w-0 rounded-lg border border-orange-500 bg-zinc-900 px-3 py-2 text-[12px] text-white"
            data-testid="qo-maps-link"
          />
          {!mapsLink && (
            <button
              type="button"
              onClick={useMyLocation}
              disabled={geoStatus === "loading"}
              className="w-full [@media(min-width:400px)]:w-auto shrink-0 px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 active:scale-95 text-white text-[11px] font-bold shadow disabled:opacity-60"
              data-testid="qo-geo-btn"
            >
              {geoStatus === "loading" ? "..." : "Sherlok"}
            </button>
          )}
        </div>
        {geoStatus === "denied" && (
          <div className="text-[10px] text-red-400 mb-3" data-testid="qo-geo-denied">
            Izin lokasi ditolak. Silakan isi manual.
          </div>
        )}
        {geoStatus === "unsupported" && (
          <div className="text-[10px] text-red-400 mb-3" data-testid="qo-geo-unsupported">
            Browser tidak mendukung lokasi otomatis. Silakan isi manual.
          </div>
        )}
        {geoStatus === "ok" && (
          <div className="text-[10px] text-emerald-400 mb-3" data-testid="qo-geo-ok">
            ✓ Lokasi berhasil terdeteksi
          </div>
        )}
        {geoStatus === "" && <div className="mb-3" />}

        {/* Notes */}
        <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-bold mb-1">
          Keterangan Tambahan (opsional)
        </label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Contoh: AC kamar lantai 2 kurang dingin..."
          rows={3}
          className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm mb-4"
          data-testid="qo-notes" />

        {/* Summary preview toggle */}
        <button onClick={() => setShowSummary(v => !v)}
          className="text-[12px] text-orange-700 underline mb-3"
          data-testid="qo-toggle-summary">
          {showSummary ? "Sembunyikan ringkasan" : "Lihat orderan"}
        </button>
        {showSummary && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-[13px] space-y-1">
            {services.filter(r => r.service).length > 1 ? (
              <div>
                <span className="text-zinc-500">Layanan:</span>
                <ul className="list-disc list-inside mt-0.5">
                  {services.filter(r => r.service).map((r, i) =>
                    <li key={i}><b translate="no">{svcLabel(r.service)}</b> — {r.qty} unit</li>
                  )}
                </ul>
              </div>
            ) : (
              <>
                <div><span className="text-zinc-500">Layanan:</span> <b translate="no">{svcLabel(services[0]?.service) || "-"}</b></div>
                <div><span className="text-zinc-500">Jumlah:</span> <b>{services[0]?.qty || 1} unit</b></div>
              </>
            )}
            <div><span className="text-zinc-500">Alamat:</span> <b>{activeAddress() || "-"}</b></div>
            {notes && <div><span className="text-zinc-500">Keterangan:</span> <b>{notes}</b></div>}
          </div>
        )}

        {/* Options after preview — v84.128: 2-column grid day+time (max 3×2=6 buttons) */}
        {preview && (Array.isArray(preview.slots) ? preview.slots : []).length > 0 && (
          <div ref={slotsRef} className="mb-4" data-testid="qo-slots-section">
            <div className="text-white font-semibold mb-3 text-center" data-testid="qo-options-title">
              Tersedia untuk Anda!<br />
              Silahkan pilih salah satu tanggal:
            </div>
            {busy && (
              <div className="flex items-center justify-center gap-2 text-orange-400 text-sm font-medium mb-2"
                   data-testid="qo-picking-hint">
                <Loader2 className="w-4 h-4 animate-spin" /> Mohon tunggu...
              </div>
            )}
            <div className="grid grid-cols-1 [@media(min-width:400px)]:grid-cols-2 gap-2" translate="no">
              {(preview.slots || []).slice(0, 6).map((s, idx) => {
                const slotKey = `${s.date}|${s.start_time}|${s.tech_id || ""}`;
                const isUnavail = unavailableSlots.has(slotKey);
                return (
                  <button
                    key={`${s.date}-${s.start_time}-${idx}`}
                    onClick={() => pickSlot(s)}
                    disabled={busy || isUnavail}
                    className={`h-14 rounded-lg active:scale-95 text-[13px] font-semibold shadow flex flex-col items-center justify-center leading-tight px-2 transition ${
                      isUnavail
                        ? "bg-zinc-600 text-zinc-400 cursor-not-allowed line-through opacity-60"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
                    }`}
                    data-testid={`qo-slot-${s.date}-${s.start_time}`}
                    title={isUnavail ? "Slot sudah tidak tersedia" : ""}
                  >
                    <span>{s.label}</span>
                    <span className="mt-0.5">{s.time_label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Fallback: legacy day-only options (jika backend belum kirim slots) */}
        {preview && (!preview.slots || preview.slots.length === 0)
            && Array.isArray(preview.options) && preview.options.length > 0 && (
          <div ref={slotsRef} className="mb-4">
            <div className="text-white font-semibold mb-3 text-center">
              Tersedia untuk Anda!<br />
              Silahkan pilih salah satu tanggal:
            </div>
            <div className="space-y-2">
              {(preview.options || []).map((o) => (
                <button key={o.date} onClick={() => pickSlot({ date: o.date, start_time: null, tech_id: null, label: o.label })}
                  disabled={busy}
                  className="w-full h-12 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-sm font-bold shadow disabled:opacity-50"
                  data-testid={`qo-date-${o.date}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {dateError && (
          <div className="mb-3 p-2 bg-red-50 border border-red-300 rounded-lg text-[12px] text-red-700">
            {dateError}
          </div>
        )}

        {/* Main CTA */}
        {!preview && (
          <>
            <button ref={orderBtnRef} onClick={submitOrder} disabled={busy}
              className="w-full h-12 rounded-lg bg-orange-600 hover:bg-orange-700 active:scale-95 text-white font-bold text-base shadow-md disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="qo-order-btn">
              {busy
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Mencarikan Slot Tersedia</>
                : <><Sparkles className="w-4 h-4" /> Order Sekarang</>
              }
            </button>
          </>
        )}

        <div className="mt-6 text-[10px] text-zinc-400 text-center">
          Butuh bantuan? Hubungi admin kami via WhatsApp.
        </div>
      </motion.div>
    </div>
  );
}
